import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { polarConfig } from '@/lib/polar-config';

export interface SubscriptionInfo {
  tier: 'free' | 'free_trial' | 'monthly_pro' | 'yearly_pro';
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  trialEndsAt: string | null;
  subscriptionEndsAt: string | null;
  isTrialExpired: boolean;
  canAccessFeature: (feature: string) => boolean;
  remainingTrialDays: number;
}

const FEATURE_ACCESS = {
  free: ['dashboard_view', 'idea_assessment_limited'], // Remove roi_tracker and founder_journal from free
  free_trial: ['dashboard_view', 'roi_tracker', 'founder_journal', 'idea_assessment_limited'],
  monthly_pro: [
    'dashboard_view', 
    'roi_tracker', 
    'founder_journal', 
    'idea_assessment', 
    'boss_mode', 
    'community_pods', 
    'playbooks',
    'social_tracker',
    'prompt_library',
    'all_features'
  ],
  yearly_pro: [
    'dashboard_view', 
    'roi_tracker', 
    'founder_journal', 
    'idea_assessment', 
    'boss_mode', 
    'community_pods', 
    'playbooks',
    'social_tracker',
    'prompt_library',
    'all_features'
  ]
};

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
    tier: 'free',
    status: 'active',
    trialEndsAt: null,
    subscriptionEndsAt: null,
    isTrialExpired: false,
    remainingTrialDays: 0,
    canAccessFeature: () => false
  });
  const [loading, setLoading] = useState(true);

  const calculateTrialDays = (trialEndDate: string | null): number => {
    if (!trialEndDate) return 0;
    const now = new Date();
    const endDate = new Date(trialEndDate);
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const canAccessFeature = useCallback((feature: string): boolean => {
    const tierAccess = FEATURE_ACCESS[subscriptionInfo.tier] || [];
    
    console.log('canAccessFeature check:', {
      feature,
      tier: subscriptionInfo.tier,
      tierAccess,
      isTrialExpired: subscriptionInfo.isTrialExpired,
      remainingTrialDays: subscriptionInfo.remainingTrialDays
    });
    
    // Special handling for idea assessment - always allow with limits
    if (feature === 'idea_assessment' || feature === 'idea_assessment_limit') {
      console.log('Idea assessment - always allowed');
      return true; // Always allow idea assessment for everyone
    }
    
    // Handle free trial access to roi_tracker and founder_journal
    if (subscriptionInfo.tier === 'free_trial' && !subscriptionInfo.isTrialExpired) {
      const hasAccess = ['roi_tracker', 'founder_journal'].includes(feature) || 
                       tierAccess.includes(feature) || 
                       tierAccess.includes('all_features');
      console.log('Free trial access check:', { feature, hasAccess });
      return hasAccess;
    }
    
    const hasAccess = tierAccess.includes(feature) || tierAccess.includes('all_features');
    console.log('Regular access check:', { feature, hasAccess });
    return hasAccess;
  }, [subscriptionInfo.tier, subscriptionInfo.isTrialExpired, subscriptionInfo.remainingTrialDays]);

  const fetchSubscriptionInfo = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching subscription info for user:', user.id);
      const { data, error } = await supabase.rpc('get_subscription_info', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Error fetching subscription info:', error);
        return;
      }

      console.log('Raw subscription data from DB:', data);

      if (data && data.length > 0) {
        const info = data[0];
        const remainingTrialDays = calculateTrialDays(info.trial_ends_at);
        const isTrialExpired = info.is_trial_expired || false;
        
        console.log('Processed subscription info:', {
          tier: info.tier,
          status: info.status,
          trialEndsAt: info.trial_ends_at,
          isTrialExpired,
          remainingTrialDays
        });
        
        const subscriptionInfo: SubscriptionInfo = {
          tier: (info.tier as 'free' | 'free_trial' | 'monthly_pro' | 'yearly_pro') || 'free',
          status: (info.status as 'active' | 'canceled' | 'past_due' | 'trialing') || 'active',
          trialEndsAt: info.trial_ends_at,
          subscriptionEndsAt: info.subscription_ends_at,
          isTrialExpired,
          remainingTrialDays,
          canAccessFeature
        };
        
        console.log('Setting subscription info:', subscriptionInfo);
        setSubscriptionInfo(subscriptionInfo);
      } else {
        console.log('No subscription data found, using free tier');
        setSubscriptionInfo({
          tier: 'free',
          status: 'active',
          trialEndsAt: null,
          subscriptionEndsAt: null,
          isTrialExpired: false,
          remainingTrialDays: 0,
          canAccessFeature
        });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }, [user, canAccessFeature]);

  const startFreeTrial = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.functions.invoke('polar-checkout', {
        body: {
          planType: 'free_trial',
          successUrl: `${window.location.origin}/app/dashboard`
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success('Free trial started! Enjoy 7 days of full access.');
        await fetchSubscriptionInfo();
        return true;
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      toast.error('Failed to start free trial');
    }
    
    return false;
  };

  const createCheckoutSession = async (planType: 'monthly_pro' | 'yearly_pro'): Promise<string | null> => {
    if (!user) return null;

    try {
      // Use Supabase Edge Function to create a Polar checkout session
      const { data, error } = await supabase.functions.invoke('polar-checkout', {
        body: {
          planType,
          successUrl: `${window.location.origin}${polarConfig.defaultSuccessUrl}`,
          cancelUrl: `${window.location.origin}${polarConfig.defaultCancelUrl}`,
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }

      const checkoutUrl = data?.checkoutUrl || data?.url || data?.redirectUrl;
      if (!checkoutUrl) {
        console.error('No checkout URL in response:', data);
        throw new Error('No checkout URL returned from server');
      }

      return checkoutUrl;
    } catch (error: unknown) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create checkout session';
      toast.error(errorMessage);
      return null;
    }
  };

  const checkIdeaAssessmentLimit = async (): Promise<{ canAssess: boolean; assessmentCount: number }> => {
    if (!user) return { canAssess: false, assessmentCount: 0 };

    try {
      const { data, error } = await supabase
        .from('idea_assessments')
        .select('id')
        .eq('user_id', user.id);

      if (error) throw error;

      const assessmentCount = data.length;
      
      // Free trial users can only do 1 assessment
      if (subscriptionInfo.tier === 'free_trial') {
        return { canAssess: assessmentCount === 0 && !subscriptionInfo.isTrialExpired, assessmentCount };
      }
      
      // Pro users have unlimited assessments
      if (subscriptionInfo.tier === 'monthly_pro' || subscriptionInfo.tier === 'yearly_pro') {
        return { canAssess: true, assessmentCount };
      }
      
      // Free users can't assess ideas
      return { canAssess: false, assessmentCount };
    } catch (error) {
      console.error('Error checking assessment limit:', error);
      return { canAssess: false, assessmentCount: 0 };
    }
  };

  useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  // Auto-refresh subscription info every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchSubscriptionInfo, 30000);
    return () => clearInterval(interval);
  }, [fetchSubscriptionInfo]);

  return {
    subscriptionInfo,
    loading,
    startFreeTrial,
    createCheckoutSession,
    manageSubscription: () => {
      // Redirect to Stripe Customer Portal
      window.open('https://billing.stripe.com/p/login/test_7sI5mW5eK3JZ5M4bII', '_blank');
    },
    checkIdeaAssessmentLimit,
    refreshSubscriptionInfo: fetchSubscriptionInfo
  };
};